import React, { useEffect, useState } from 'react';
import { Alert, Container, Pagination, Snackbar, Stack } from '@mui/material';
import { listAllCategories } from '../../api/products';
import AddCategory from '../../components/categories/AddCategory';
import DashboardLayout from '../../layout/dashboard/DashboardLayout';
import { categoryFields } from '../../staticData/tableHead';
import useAuthToken from '../../hooks/getUserData';
import CategoryListTable from '../../components/categories/ListTable';

const Categories = () => {
  const token = useAuthToken();

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 15;
  const [totalPages, setTotalPages] = useState(0);

  const fetchData = async (pageNumber) => {
    try {
      if (token) {
        setLoading(true);
        const pageToFetch = pageNumber !== undefined ? pageNumber : page;
        const response = await listAllCategories(token, pageSize, pageToFetch, searchTerm);

        if (!response.ok) {
          setError('Failed to fetch categories');
        } else {
          const data = await response.json();

          if (data?.categories) {
            setItems(data.categories);
            setError(null);
            setTotalPages(Math.ceil(data.count / pageSize));
          }
        }
      }
    } catch (err) {
      setError(err.message || 'An error occurred while fetching categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchData(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, searchTerm]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchData(newPage + 1);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    fetchData(0);
    setPage(0);
  };

  return (
    <DashboardLayout>
      <Container maxWidth="xl">
        <Stack spacing={2}>
            <>
              {error ? (
                <Alert severity="error" autoHideDuration={6000}>
                  {error}
                </Alert>
              ) : (
                <>
                  <Snackbar
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    open={snackbarOpen}
                    autoHideDuration={3000}
                    onClose={() => setSnackbarOpen(false)}>
                    <Alert severity="success" sx={{ width: '100%' }}>
                      {snackbarMessage}
                    </Alert>
                  </Snackbar>
                  <CategoryListTable
                    filteredData={items}
                    fetchData={fetchData}
                    columnFields={categoryFields}
                    tableName={'Categories'}
                    setMessage={setSnackbarMessage}
                    setSnackbarOpen={setSnackbarOpen}
                    page={page}
                    loading={loading}
                    pageSize={pageSize}
                    setPage={setPage}
                    searchTerm={searchTerm}
                    handleSearch={handleSearch}
                    addModal={
                      <AddCategory
                        fetchData={fetchData}
                        setMessage={setSnackbarMessage}
                        setSnackbarOpen={setSnackbarOpen}
                      />
                    }
                  />
                  {totalPages > 1 && (
                    <Pagination
                      count={totalPages}
                      page={page + 1}
                      onChange={(event, newPage) => handlePageChange(newPage - 1)}
                      sx={{ alignSelf: 'center' }}
                    />
                  )}
                </>
              )}
            </>
        </Stack>
      </Container>
    </DashboardLayout>
  );
};

export default Categories;
